import { createStore, createTypedHooks, persist } from 'easy-peasy'
import { appModel } from './state'

/**
 * App State Store
 *
 * PERSISTENCE — read this before changing it.
 *
 * `persist()` is applied to each SLICE, not to the root model, and that is
 * deliberate: easy-peasy resolves `allow`/`deny` against the keys of whatever
 * you wrapped, so a root-level `persist` can only name `user` / `appSettings`
 * and has no way to exclude a field INSIDE a slice.
 *
 * What that buys: `appSettings.jobs` (the whole board) and `similarJobs` are no
 * longer written to sessionStorage. The persist middleware runs on EVERY
 * action, serialising the wrapped slice each time — measured at the ~5,000-record
 * scale that was a **3.1 MB payload, ~10ms to JSON.stringify on a dev box** (and
 * several times that on a shop-floor tablet) on every keystroke in the search
 * box, every filter chip and every board refresh. It was also approaching the
 * ~5 MB per-origin sessionStorage quota, past which writes throw.
 *
 * Why dropping `jobs` is safe now: the only screen that read the board list out
 * of persisted state was `JobPage`, whose ribbon did `jobs.find(...)`. It now
 * fetches its own card from `api/jobs/{job}/card` and falls back to the store
 * list only while that request is in flight. Nothing else needs the board to
 * survive a reload — the boards fetch on mount.
 *
 * If you add a large collection to a slice, DENY IT HERE.
 */

/** sessionStorage key used by the previous root-level `persist`. */
const LEGACY_PERSIST_KEY = '[csm][0]'

/**
 * Drop the pre-slice-persistence entry.
 *
 * Moving `persist` from the root to the slices changes the storage keys
 * (`[csm][0]` → `[csm][0][user]` / `[csm][0][appSettings]`), so on the first
 * load after this ships the old entry is orphaned — and it still holds the
 * multi-megabyte board that this change exists to stop writing. Nothing would
 * ever clear it, so it would squat on the quota for the rest of the session.
 *
 * Best-effort: sessionStorage can throw (disabled cookies, private mode), and
 * failing to tidy up must never stop the app from starting.
 */
try {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(LEGACY_PERSIST_KEY)
  }
} catch {
  // Ignore — see above.
}

const storeModel = {
  user: persist(appModel.user, {
    // Storage options (see https://easy-peasy.now.sh/docs/api/persist.html)
    storage: 'sessionStorage', // sessionStorage | localStorage
  }),
  appSettings: persist(appModel.appSettings, {
    storage: 'sessionStorage',
    // Server-derived collections, re-fetched on mount. Everything else here is
    // small UI state (filters, mode toggles) worth keeping across a reload.
    deny: ['jobs', 'similarJobs'],
  }),
}

/**
 * Export Typed Hooks
 *
 * Creating and exporting typed versions of the hooks means we don't need to
 * apply typing information against them when using them in our components.
 */
export const { useStore, useStoreState, useStoreActions, useStoreDispatch } =
  createTypedHooks<typeof storeModel>()

/**
 * Export Store
 *
 * The values are retained in session storage until the user closes the browser.
 * If necessary a slice can be persisted to local storage instead, so its values
 * survive the browser closing.
 */
export default createStore(storeModel, {
  // Store Config (see https://easy-peasy.now.sh/docs/api/store-config.html)
  name: 'csm', // Name the store using our app name
})
